import React, { useEffect, useState } from "react";

import QRCode from "react-qr-code";
import Container from "@mui/material/Container";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";

import { isMobile } from "react-device-detect";

import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";

import { showError, showOptions } from "../../components/ATPDialogs";
import {
  getBathroomPass,
  encryptLeavePass,
  getPassTypes,
  PASS_QR_KEY,
} from "../../services/PassService";
import { getQrBaseUrl } from "../../services/QrService";
import {
  StripedTableCell,
  StripedTableHeadCell,
  StripedTableRow,
} from "../../components/StripedTable";

import SETTINGS from "../../settings.json";
import { Alert } from "@mui/material";
import { getLocations } from "../../services/SchoolService";
import isEmpty from "lodash-es/isEmpty";
import { getUserSettings } from "../../services/UserService";
import HeaderText from "../../components/HeaderText";

const PassLeaveQr = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);
  const [_location, setLocation] = useState({});
  const [_passType, setPassType] = useState({});
  const [_searchPerformed, setSearchPerformed] = useState(false);
  const [_qrUrl, setQrUrl] = useState("");
  const [_baseUrl, setBaseUrl] = useState("");
  const [_passTypes, setPassTypes] = React.useState([]);
  const [_selectedPassTypeEid, setSelectedPassTypeEid] = React.useState("");
  const [_locations, setLocations] = useState([]);
  const [, setUserSettings] = useState({});

  const [_teacher, setTeacher] = useState({
    eid: "",
    last_name: "",
    first_name: "",
  });

  useEffect(() => {
    initContent();
  }, []);

  const initContent = () => {
    setShowProgress(true);

    const promises = [
      getBathroomPass(_school, _jwt),
      getQrBaseUrl(_school, _jwt),
      getPassTypes(_school, _jwt),
      getLocations(_school, _jwt),
      getUserSettings(_school, _jwt)
    ];

    Promise.all(promises).then((results) => {
      let result = results[0];

      if (result.detail != null) {
        showError("Unable to Generate QR", result.detail);
        return;
      }

      setTeacher(result.data.teacher);
      setLocation(result.data.location);
      setPassType(result.data.pass_type);
      setSelectedPassTypeEid(result.data.pass_type.eid);

      const baseUrl = results[1];
      setBaseUrl(baseUrl);

      // initialize with the default values
      initQrUrl(
        baseUrl,
        result.data.location.eid,
        result.data.teacher.eid,
        result.data.pass_type.eid
      );

      setShowProgress(false);
      setSearchPerformed(true);

      const passTypes = results[2].data;
      setPassTypes(passTypes);

      const locations = results[3].data;
      setLocations(locations);

      const userSettings = results[4].data;
      if (userSettings.defaultBathroomLocationEid != null) {
        for (const location of locations) {
          if (location.eid === userSettings.defaultBathroomLocationEid) {
            setLocation(location);
            break;
          }
        }
      }

      if (userSettings.defaultBathroomPassTypeEid != null) {
        setSelectedPassTypeEid(userSettings.defaultBathroomPassTypeEid);
        // set the object if pass type is specified
        for (const passType of passTypes) {
          if (passType.eid === userSettings.defaultBathroomPassTypeEid) {
            setPassType(passType);
            break;
          }
        }
      }

      setUserSettings(userSettings);
    });
  };

  const initQrUrl = (baseUrl, locationEid, teacherEid, passTypeEid) => {
    const encrypted = encryptLeavePass(
      PASS_QR_KEY,
      locationEid,
      teacherEid,
      passTypeEid
    );

    setQrUrl(baseUrl + `/student/pass/qr/leave/${encrypted}`);
  };

  const getQrCode = () => {
    if (isMobile) {
      return (
        <QRCode
          size={256}
          style={{ height: "auto", maxWidth: "80%", width: "80%" }}
          value={_qrUrl}
          viewBox={`0 0 256 256`}
        />
      );
    } else {
      return <QRCode size={580} value={_qrUrl} />;
    }
  };

  const getPassTypeOptions = () => {
    const map = {};
    for (let passType of _passTypes) {
      map[passType.eid] = passType.name;
    }
    return map;
  };

  const getPassTypeByEid = (eid) => {
    for (const passType of _passTypes) {
      if (passType.eid === eid) {
        return passType;
      }
    }
    return null;
  };

  const onClickPassType = async () => {
    const passTypeResult = await showOptions(
      "Select a Pass Type",
      "Click Pass Type Name",
      getPassTypeOptions()
    );
    if (!passTypeResult.isConfirmed) {
      return;
    }
    if (isEmpty(passTypeResult.value)) {
      return;
    }

    const selectedPassTypeObj = getPassTypeByEid(passTypeResult.value);
    setPassType(selectedPassTypeObj);

    if (selectedPassTypeObj != null) {
      if (selectedPassTypeObj.defaultLocationEid != null) {
        setLocation(getLocationByEid(selectedPassTypeObj.defaultLocationEid));
      }
    }

    initQrUrl(_baseUrl, _location.eid, _teacher.eid, selectedPassTypeObj.eid);
  };

  const getLocationOptions = () => {
    const map = {};
    for (let location of _locations) {
      map[location.eid] = location.name;
    }
    return map;
  };

  const getLocationByEid = (eid) => {
    for (const location of _locations) {
      if (location.eid === eid) {
        return location;
      }
    }
    return null;
  };

  const onClickLocation = async () => {
    const locationResult = await showOptions(
      "Select a Location",
      "Click Location",
      getLocationOptions()
    );
    if (!locationResult.isConfirmed) {
      return;
    }
    if (isEmpty(locationResult.value)) {
      return;
    }

    const selectedLocationObj = getLocationByEid(locationResult.value);
    setLocation(selectedLocationObj);

    initQrUrl(
      _baseUrl,
      selectedLocationObj.eid,
      _teacher.eid,
      _selectedPassTypeEid
    );
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Bath/Leave QR Code" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <Box sx={{ display: _searchPerformed ? "" : "none" }}>
          <Alert severity="info" sx={{ mb: 1 }} className="noprint">
            You can now click the Pass Type and Location values to change them.
            The QR code will regenerate automatically.
          </Alert>

          <HeaderText text={`Bath/Leave QR Code`} />
          <TableContainer component={Paper}>
            <Table size="small">
              <TableBody>
                <StripedTableRow>
                  <StripedTableHeadCell>Pass Type:</StripedTableHeadCell>
                  <StripedTableCell>
                    <Link component="button" onClick={onClickPassType}>
                      {_passType.name}
                    </Link>
                  </StripedTableCell>
                  <StripedTableHeadCell>Location:</StripedTableHeadCell>
                  <StripedTableCell>
                    <Link component="button" onClick={onClickLocation}>
                      {_location.name}
                    </Link>
                  </StripedTableCell>
                </StripedTableRow>
                <StripedTableRow>
                  <StripedTableHeadCell>Teacher:</StripedTableHeadCell>
                  <StripedTableCell>
                    {_teacher.last_name}, {_teacher.first_name}
                  </StripedTableCell>
                </StripedTableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <p style={{ textAlign: "center" }}>{getQrCode()}</p>
          <p>
            <a href={_qrUrl}>Link</a> - {SETTINGS.version}
          </p>
        </Box>
      </Container>
    </>
  );
};

export default PassLeaveQr;
